import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { testTranslate } from "@/lib/translate.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FlaskConical } from "lucide-react";

export const Route = createFileRoute("/_authenticated/settings")({ component: Page });

function Page() {
  const qc = useQueryClient();
  const tr = useServerFn(testTranslate);
  const [testText, setTestText] = useState("Gold prices surged to a new all-time high amid Fed rate cut expectations.");
  const [testResult, setTestResult] = useState("");

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const { data } = await supabase.from("app_settings").select("*");
      const map: any = {}; (data ?? []).forEach((r: any) => map[r.key] = r.value); return map;
    },
  });
  const set = async (key: string, value: any) => {
    const { error } = await supabase.from("app_settings").upsert({ key, value: value as any }, { onConflict: "key" } as any);
    if (error) return toast.error(error.message);
    toast.success("บันทึก");
    qc.invalidateQueries({ queryKey: ["settings"] });
  };
  const provider = settings?.translation_provider ?? "lovable_ai";
  const model = settings?.translation_model ?? "google/gemini-3-flash-preview";
  const dedup = settings?.dedup_enabled ?? true;
  const defReq = settings?.default_requires_approval ?? false;

  const doTest = async () => {
    try { toast.loading("ทดสอบแปล...", { id: "t" }); const r: any = await tr({ data: { text: testText } }); setTestResult(r.result); toast.success("สำเร็จ", { id: "t" }); }
    catch (e: any) { toast.error(e.message, { id: "t" }); }
  };

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-display font-semibold">Settings</h1><p className="text-sm text-muted-foreground">ตั้งค่าผู้ให้บริการแปลภาษาและนโยบายระบบ</p></div>

      <Card>
        <CardHeader><CardTitle>Translation Provider</CardTitle><CardDescription>เริ่มต้นด้วย Lovable AI (Gemini) — สลับเป็น OpenAI / DeepL ได้</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2"><Label>Provider</Label>
              <Select value={provider} onValueChange={(v) => set("translation_provider", v)}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lovable_ai">Lovable AI (Gemini) — แนะนำ</SelectItem>
                  <SelectItem value="openai">OpenAI</SelectItem>
                  <SelectItem value="deepl">DeepL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Model</Label>
              <Input value={model} onChange={(e) => qc.setQueryData(["settings"], (old: any) => ({ ...old, translation_model: e.target.value }))} onBlur={(e) => set("translation_model", e.target.value)}/>
              <p className="text-xs text-muted-foreground">เช่น google/gemini-3-flash-preview, openai/gpt-5-mini, gpt-4o-mini</p>
            </div>
          </div>
          {provider === "openai" && (<div className="space-y-2"><Label>OpenAI API Key</Label><Input type="password" defaultValue={settings?.openai_api_key ?? ""} onBlur={(e) => set("openai_api_key", e.target.value)}/></div>)}
          {provider === "deepl" && (<div className="space-y-2"><Label>DeepL API Key</Label><Input type="password" defaultValue={settings?.deepl_api_key ?? ""} onBlur={(e) => set("deepl_api_key", e.target.value)}/></div>)}

          <div className="border-t border-border pt-4 space-y-3">
            <Label>ทดสอบแปล</Label>
            <Textarea value={testText} onChange={(e) => setTestText(e.target.value)} rows={3}/>
            <Button onClick={doTest} variant="outline"><FlaskConical className="h-3 w-3 mr-1"/>Test Translate</Button>
            {testResult && <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-sm whitespace-pre-wrap">{testResult}</div>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>นโยบายระบบ</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between"><div><Label>ป้องกันข่าวซ้ำ (Content Hash + URL)</Label><p className="text-xs text-muted-foreground">แนะนำให้เปิดไว้</p></div><Switch checked={!!dedup} onCheckedChange={(v) => set("dedup_enabled", v)}/></div>
          <div className="flex items-center justify-between"><div><Label>Default: ต้องอนุมัติก่อนส่ง</Label><p className="text-xs text-muted-foreground">ใช้เมื่อ Category ไม่ได้ระบุ</p></div><Switch checked={!!defReq} onCheckedChange={(v) => set("default_requires_approval", v)}/></div>
        </CardContent>
      </Card>
    </div>
  );
}